// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { isLocalhost, tabClickHandler, toDropdownItems } from "guiutils";
import { IAction, ActionType, raiseAction, IActionTypeLogin, IActionTypeSignUp } from "actions";
import { DataState, RootPath } from "types";
import { IState } from "states/states";
import { AppState } from "states/schedulerstates";

import { ReactComponent as IconConnect } from "static/imgDisconnect.svg";
import Dropdown from "view/comp/Dropdown";
import AppStateView from "view/comp/AppState";

import "./Login.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface ILoginProps {
  isRfidLogin: boolean;
  appState: AppState;
  loginDataState: DataState;
  parameterDataState: DataState;
  userEmail: string | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ILoginProps = {
    isRfidLogin: state.router.rootPath === RootPath.RFID,
    appState: state.scheduler.appState,
    userEmail: state.gui.login.email,
    loginDataState: state.scheduler.loginDataState,
    parameterDataState: state.scheduler.parameterDataState,
  };
  return props;
};

interface ILoginState {
  username: string;
  password: string;
  isNfcReadingStarted?: boolean;
}

interface ILoginDispatch {
  onForgotPwd: (username: string) => void;
  onLoginClick: (username: string, password: string) => void;
  onSingUpClick: (username: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ILoginDispatch = {
    onForgotPwd: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD, username));
    },
    onLoginClick: (username: string, password: string) => {
      const payload: IActionTypeLogin = {
        username,
        password,
      };
      dispatch(raiseAction(ActionType.ACT_LOGIN_REQ, payload));
    },
    onSingUpClick: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_SIGNUP, username));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_user_caption: {
    id: "Select user",
  },
  rfidInvalid: {
    id: "This charger card number is not allowed to login.",
  },
});

class LoginComponent extends React.Component<
  ILoginProps & ILoginDispatch & WrappedComponentProps,
  ILoginState
> {
  state: ILoginState = {
    username: IS_CLOUD ? this.props.userEmail ?? "" : "operator",
    password: this.props.isRfidLogin ? "17111976RM" : "",
  };

  public render() {
    return (
      <div id="login">
        <div id="login_inner">
          <IconConnect width={50} />
          <div id="login_right">
            {this.renderLoginForm()}
            <AppStateView />
          </div>
        </div>
      </div>
    );
  }

  private isUsernameValid() {
    // check for valid RFID number
    if (IS_CLOUD && this.props.isRfidLogin) {
      // at least 6 hexadecimal digits, potentially with leading # for Etrel pins
      return /^#?[0-9a-fA-F]{6,}$/.test(this.state.username);
    }
    return true;
  }

  private renderLoginForm() {
    const users = ["admin", "operator"];
    const userItems = toDropdownItems(
      users,
      "username",
      (_name, value) => {
        this.setState({ username: value });
      },
      (item: string) => item,
    );
    const hasNfcReading = this.props.isRfidLogin && (window as any).NDEFReader;

    return (
      <>
        <h1>
          <FormattedMessage id="Login" />
        </h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <table>
            <tbody>
              <tr>
                <td>
                  {this.props.isRfidLogin ? (
                    <FormattedMessage id="RFID" />
                  ) : IS_CLOUD ? (
                    <FormattedMessage id="Email" />
                  ) : (
                    <FormattedMessage id="User" />
                  )}
                </td>
                <td>
                  {IS_CLOUD ? (
                    <input
                      type="text"
                      name="username"
                      title={
                        this.isUsernameValid() || this.state.username.length === 0
                          ? undefined
                          : this.props.intl.formatMessage(messages.rfidInvalid)
                      }
                      className={
                        this.isUsernameValid() || this.state.username.length === 0
                          ? undefined
                          : "input-error"
                      }
                      value={this.state.username}
                      autoFocus
                      disabled={this.props.loginDataState === DataState.SAVE_REQ}
                      onChange={(event) => {
                        this.setState({ username: event?.currentTarget.value });
                      }}
                    />
                  ) : isLocalhost() ? (
                    <>admin</>
                  ) : (
                    <Dropdown
                      className="btn-group-sm"
                      items={userItems}
                      caption={this.props.intl.formatMessage(messages.msg_user_caption)}
                      curItem={this.state.username}
                      disabled={this.props.loginDataState === DataState.SAVE_REQ}
                    />
                  )}
                </td>
              </tr>
              {!this.props.isRfidLogin && (
                <tr>
                  <td>
                    <FormattedMessage id="Password" />:
                  </td>
                  <td>
                    <input
                      name="password"
                      type="password"
                      value={this.state.password}
                      disabled={this.props.loginDataState === DataState.SAVE_REQ}
                      onChange={(event) => {
                        this.setState({ password: event?.currentTarget.value });
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-3">
            <Button
              id="perform-login"
              type="submit"
              variant="primary"
              onClick={() => {
                const username = !IS_CLOUD && isLocalhost() ? "admin" : this.state.username;
                this.props.onLoginClick(username, this.state.password);
              }}
              disabled={this.props.loginDataState === DataState.SAVE_REQ || !this.isUsernameValid()}
            >
              <FormattedMessage id="Perform Login" />
            </Button>
            {!IS_CLOUD && isLocalhost() && (
              <Button
                variant="secondary"
                id="cancel-login"
                className="ms-2"
                onClick={(_evt) => window.location.reload()}
              >
                <FormattedMessage id="Cancel" />
              </Button>
            )}
            {IS_CLOUD &&
              !this.props.isRfidLogin &&
              this.props.loginDataState !== DataState.SAVE_REQ && (
                <span
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  className="text-link ms-3"
                  onClick={() => this.props.onForgotPwd(this.state.username)}
                >
                  <FormattedMessage id="Forgot Password?" />
                </span>
              )}
            {IS_CLOUD &&
              !this.props.isRfidLogin &&
              this.props.loginDataState !== DataState.SAVE_REQ && (
                <>
                  <div className="mb-4"></div>
                  <div className="loginOptions">
                    <div className="link" style={{ marginTop: 11 }}></div>
                    <span
                      tabIndex={0}
                      onKeyDown={tabClickHandler}
                      className="text-link ms-3"
                      onClick={(event) => {
                        this.props.onSingUpClick(this.state.username);
                      }}
                    >
                      <FormattedMessage id="Sign-Up here..." />
                    </span>
                  </div>
                  <div className="mb-4"></div>
                </>
              )}{" "}
            {hasNfcReading &&
              this.props.loginDataState !== DataState.SAVE_REQ &&
              (this.state.isNfcReadingStarted ? (
                <span className="ms-3">
                  <FormattedMessage id="Hold NFC card to reader." />
                </span>
              ) : (
                <span
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  className="text-link ms-3"
                  onClick={async () => await this.onScanNfcCard()}
                >
                  <FormattedMessage id="Scan NFC card" />
                </span>
              ))}
          </div>
        </form>
      </>
    );
  }

  private async onScanNfcCard() {
    try {
      const ndef = new NDEFReader();
      await ndef.scan();
      this.setState({ isNfcReadingStarted: true });
      ndef.addEventListener("readingerror", () => {
        console.log("NFC reading error.");
      });
      ndef.addEventListener("reading", (event) => {
        const serialNumber = (event as any).serialNumber;
        if (serialNumber) {
          // remove ":" characters from serial number
          this.setState({ username: serialNumber.replace(/:/g, "") });
        } else {
          console.log("NFC reading result has no serial number.", event);
        }
      });
    } catch (error) {
      console.log("Scanning NFC error", error);
    }
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(LoginComponent));
